import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import type { TAssignableRole } from '@conversed/shared/src/central/types/role.types';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoleDraft } from './hooks/useRoleDraft';
import { getRoleDescriptionKey, getRoleIcon } from './users.constants';

type TUserRoleSelectVariant = 'inline' | 'field';

type TUserRoleSelectProps = {
  roles: TAssignableRole[];
  /**
   * The roles the dropdown offers. Defaults to `roles`. The current role label
   * always comes from `roles`, so a role outside this list still shows.
   */
  selectableRoles?: TAssignableRole[];
  selectedRoleIds: string[];
  /**
   * `inline` calls it once when the dropdown closes, only when the selection
   * changed and is not empty. `field` calls it on every toggle.
   */
  onChange: (roleIds: string[]) => void;
  isLocked?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  variant?: TUserRoleSelectVariant;
  placeholder?: string;
  hasError?: boolean;
  id?: string;
};

const BUTTON_VARIANT_CLASSES: Record<TUserRoleSelectVariant, string> = {
  inline:
    'border px-2 py-1 md:min-w-[150px] text-tiny text-gray-700 hover:border-gray-400 focus-visible:border-primary-base',
  // * Matches the `InputT` frame, so the field lines up with the inputs above it.
  field:
    'w-full px-2.5 py-1.5 text-xs leading-5 text-gray-600 ring-1 ring-inset focus-visible:ring-primary-base',
};

const getBorderClass = (
  variant: TUserRoleSelectVariant,
  hasError: boolean,
): string => {
  if (variant === 'field') {
    if (hasError) {
      return 'ring-red-base focus-visible:ring-red-base';
    }

    return 'ring-gray-200';
  }

  if (hasError) {
    return 'border-red-base';
  }

  return 'border-gray-300';
};

type TRoleLabelProps = {
  selectedRoles: TAssignableRole[];
  placeholder?: string;
};

// * One chip per role, the same for one role or several, so every row of the
// * table looks alike.
const RoleLabel: React.FC<TRoleLabelProps> = ({
  selectedRoles,
  placeholder,
}) => {
  if (selectedRoles.length === 0) {
    return <span className='truncate text-gray-400'>{placeholder}</span>;
  }

  return (
    <span className='flex flex-wrap items-center gap-1'>
      {selectedRoles.map((role) => (
        <span
          key={role.id}
          className='inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-px text-pico text-gray-600'>
          <MaterialIcon
            icon={getRoleIcon(role.id)}
            classes={['text-pico', '!leading-none', 'text-gray-400']}
          />
          {role.title}
        </span>
      ))}
    </span>
  );
};

type TListboxCloseWatcherProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ListboxCloseWatcher: React.FC<TListboxCloseWatcherProps> = ({
  isOpen,
  onClose,
}) => {
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      onClose();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, onClose]);

  return null;
};

export const UserRoleSelect: React.FC<TUserRoleSelectProps> = ({
  roles,
  selectableRoles,
  selectedRoleIds,
  onChange,
  isLocked = false,
  isReadOnly = false,
  isDisabled = false,
  variant = 'inline',
  placeholder,
  hasError = false,
  id,
}) => {
  const { t } = useTranslation();

  const selectedRoles = useMemo(() => {
    return roles.filter((role) => selectedRoleIds.includes(role.id));
  }, [roles, selectedRoleIds]);

  const optionRoles = useMemo(() => {
    if (selectableRoles) {
      return selectableRoles;
    }

    return roles;
  }, [roles, selectableRoles]);

  const isBuffered = variant === 'inline';

  const optionRoleIds = useMemo(() => {
    return optionRoles.map((role) => role.id);
  }, [optionRoles]);

  const {
    savedRoleIds,
    draftRoleIds,
    setDraftRoleIds,
    isDraftWithoutRoles,
    draftLabelRoles,
    commitDraft,
    discardDraftOnEscape,
  } = useRoleDraft({
    roles,
    selectedRoleIds,
    optionRoleIds,
    isDisabled,
    onCommit: onChange,
  });

  const listboxValue = useMemo(() => {
    if (isBuffered) {
      return draftRoleIds;
    }

    return savedRoleIds;
  }, [draftRoleIds, savedRoleIds, isBuffered]);

  const labelRoles = useMemo(() => {
    if (isBuffered) {
      return draftLabelRoles;
    }

    return selectedRoles;
  }, [draftLabelRoles, isBuffered, selectedRoles]);

  const handleListboxChange = useCallback(
    (roleIds: string[]) => {
      if (isBuffered) {
        setDraftRoleIds(roleIds);

        return;
      }

      onChange(roleIds);
    },
    [isBuffered, onChange, setDraftRoleIds],
  );

  if (isLocked) {
    return (
      <GeneralTooltip
        text={t('users.role.locked-tooltip')}
        placement='left'
        parentClassName='inline-flex'>
        <span className='inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-tiny text-gray-500 md:min-w-[150px]'>
          <MaterialIcon
            icon='lock'
            classes={[
              'w-4',
              'text-center',
              'text-sm',
              'leading-none',
              'text-gray-400',
            ]}
          />
          <RoleLabel selectedRoles={selectedRoles} placeholder='-' />
        </span>
      </GeneralTooltip>
    );
  }

  if (isReadOnly) {
    return (
      <span className='inline-flex px-2 py-1 text-tiny text-gray-600'>
        <RoleLabel selectedRoles={selectedRoles} placeholder='-' />
      </span>
    );
  }

  return (
    <Listbox
      value={listboxValue}
      onChange={handleListboxChange}
      disabled={isDisabled}
      multiple>
      {({ open }) => (
        <>
          {isBuffered && (
            <ListboxCloseWatcher isOpen={open} onClose={commitDraft} />
          )}
          <ListboxButton
            id={id}
            aria-label={t('users.role.select-label')}
            className={clsx(
              'inline-flex items-center justify-between gap-1 rounded-md bg-white text-left outline-none transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              BUTTON_VARIANT_CLASSES[variant],
              getBorderClass(variant, hasError),
            )}>
            <RoleLabel selectedRoles={labelRoles} placeholder={placeholder} />
            <MaterialIcon
              icon='arrow_drop_down'
              classes={['text-base', 'leading-none', 'text-gray-500']}
            />
          </ListboxButton>
          <ListboxOptions
            anchor='bottom start'
            onKeyDownCapture={isBuffered ? discardDraftOnEscape : undefined}
            className='z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white py-1 text-xs shadow-xl outline-none [--anchor-gap:4px]'>
            {optionRoles.map((role) => (
              <ListboxOption
                key={role.id}
                value={role.id}
                className='group flex cursor-pointer items-start gap-2 px-3 py-2 data-[focus]:bg-primary-hover data-[selected]:bg-primary-hover'>
                <MaterialIcon
                  icon={getRoleIcon(role.id)}
                  classes={[
                    'mt-0.5',
                    'text-sm',
                    '!leading-none',
                    'text-gray-400',
                  ]}
                />
                <span className='flex-1'>
                  <span className='block font-medium text-gray-700'>
                    {role.title}
                  </span>
                  <span className='block text-tiny text-gray-400'>
                    {t(getRoleDescriptionKey(role.id), { defaultValue: '' })}
                  </span>
                </span>
                <MaterialIcon
                  icon='done'
                  classes={[
                    'invisible',
                    'text-base',
                    'leading-none',
                    'text-primary-base',
                    'group-data-[selected]:visible',
                  ]}
                />
              </ListboxOption>
            ))}
            {isBuffered && isDraftWithoutRoles && (
              <p className='px-3 py-2 text-pico text-gray-400 italic'>
                {t('users.role.at-least-one')}
              </p>
            )}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};

export default UserRoleSelect;
